import React, { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Box, Button, TextField, MenuItem, Select, FormControl, InputLabel, Checkbox
} from '@mui/material';
import api from '../../services/api';
import {
  Add,
  Remove,
  ArrowBack
} from '@mui/icons-material';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const ItemIssue = () => {
  const [rows, setRows] = useState([]);
  const [karigarFilter, setKarigarFilter] = useState('');
  const [karatFilter, setKaratFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('both');
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [issueDetails, setIssueDetails] = useState({});
  const [selected, setSelected] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch both regular and direct orders
      const [ordersRes, directOrdersRes, karigarsRes] = await Promise.all([
        api.get('/api/shop/orders'),
        api.get('/api/directorders'),
        api.get('/api/karigar')
      ]);
      setKarigars(karigarsRes.data);
      // Flatten items: despatched but not yet issued
      const itemRows = [
        ...ordersRes.data.flatMap(order =>
          (order.items || [])
            .filter(item => item.despatchStatus && !item.workIssued)
            .map(item => ({
              billName: order.billName,
              billNumber: order.billNumber,
              itemName: item.itemName,
              karigar: item.karigarAbbreviation || '',
              karat: item.karat,
              itemWeight: item.itemWeight,
              quantity: item.quantity,
              orderId: order._id,
              itemId: item._id
            }))
        ),
        ...directOrdersRes.data.flatMap(order =>
          (order.items || [])
            .filter(item => item.despatchStatus && !item.workIssued)
            .map(item => ({
              billName: order.billName,
              billNumber: order.billNumber,
              itemName: item.itemName,
              karigar: item.karigarAbbreviation || '',
              karat: item.karat,
              itemWeight: item.itemWeight,
              quantity: item.quantity,
              orderId: order._id,
              itemId: item._id,
              isDirect: true
            }))
        )
      ];
      setRows(itemRows);
    } catch (err) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (itemKey, field, value) => {
    setIssueDetails(details => ({
      ...details,
      [itemKey]: {
        ...details[itemKey],
        [field]: value
      }
    }));
  };

  const handleSelect = (itemKey, checked) => {
    setSelected(sel => {
      if (checked) {
        // Always set date to device date when selected
        setIssueDetails(details => ({
          ...details,
          [itemKey]: {
            ...details[itemKey],
            date: new Date().toISOString().slice(0, 10)
          }
        }));
        return [...sel, itemKey];
      } else {
        return sel.filter(k => k !== itemKey);
      }
    });
  };

  const handleSelectAll = (checked, filteredRows) => {
    if (checked) {
      setSelected(filteredRows.map(row => row.orderId + '-' + row.itemId));
    } else {
      setSelected([]);
    }
  };

  const handleIssueSelected = async () => {
    try {
      // Group selected items by orderId for efficient backend update
      const itemsByOrder = {};
      const itemsByDirectOrder = {};
      selected.forEach(itemKey => {
        const [orderId, itemId] = itemKey.split('-');
        const isDirect = rows.find(row => row.orderId === orderId && row.itemId === itemId)?.isDirect;
        if (isDirect) {
          if (!itemsByDirectOrder[orderId]) itemsByDirectOrder[orderId] = [];
          itemsByDirectOrder[orderId].push(itemId);
        } else {
          if (!itemsByOrder[orderId]) itemsByOrder[orderId] = [];
          itemsByOrder[orderId].push(itemId);
        }
      });
      // Update regular orders
      for (const orderId of Object.keys(itemsByOrder)) {
        const orderRes = await api.get(`/api/shop/orders/${orderId}`);
        const order = orderRes.data;
        const updatedItems = order.items.map(item => {
          const itemKey = orderId + '-' + item._id;
          if (itemsByOrder[orderId].includes(String(item._id))) {
            return {
              ...item,
              _id: item._id,
              workIssued: true,
              issueDate: issueDetails[itemKey]?.date || new Date().toISOString().slice(0, 10),
              issueRemarks: issueDetails[itemKey]?.remarks || ''
            };
          }
          return item;
        });
        await api.put(`/api/shop/orders/${orderId}`, { ...order, items: updatedItems });
      }
      // Update direct orders
      for (const orderId of Object.keys(itemsByDirectOrder)) {
        const orderRes = await api.get(`/api/directorders/${orderId}`);
        const order = orderRes.data;
        const updatedItems = order.items.map(item => {
          const itemKey = orderId + '-' + item._id;
          if (itemsByDirectOrder[orderId].includes(String(item._id))) {
            return {
              ...item,
              _id: item._id,
              workIssued: true,
              issueDate: issueDetails[itemKey]?.date || new Date().toISOString().slice(0, 10),
              issueRemarks: issueDetails[itemKey]?.remarks || ''
            };
          }
          return item;
        });
        await api.put(`/api/directorders/${orderId}`, { ...order, items: updatedItems });
      }
      setSelected([]);
      await fetchData();
    } catch (err) {
      setError('Failed to issue selected items');
    }
  };

  const filteredRows = rows.filter(row => {
    const karigarMatch = !karigarFilter || row.karigar === karigarFilter;
    const karatMatch = !karatFilter || row.karat === karatFilter;
    const sourceMatch =
      sourceFilter === 'both' ? true :
      sourceFilter === 'order' ? !row.isDirect :
      sourceFilter === 'direct' ? row.isDirect : true;
    return karigarMatch && karatMatch && sourceMatch;
  });
  const allSelected = filteredRows.length > 0 && filteredRows.every(row => selected.includes(row.orderId + '-' + row.itemId));

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary" sx={{ mb: 2 }}>
          Back
        </Button>
        
        <Typography variant="h4" gutterBottom color="primary">
          Item Issue Management
        </Typography>
        {/* Source Filter Row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Source</InputLabel>
            <Select value={sourceFilter} label="Source" onChange={e => setSourceFilter(e.target.value)}>
              <MenuItem value="both">Both</MenuItem>
              <MenuItem value="order">Order</MenuItem>
              <MenuItem value="direct">Direct Order</MenuItem>
            </Select>
          </FormControl>
          {/* Existing karigar/karat filters can be added here if needed */}
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selected.length > 0 && !allSelected}
                        onChange={e => handleSelectAll(e.target.checked, filteredRows)}
                      />
                    </TableCell>
                    <TableCell>Bill Name</TableCell>
                    <TableCell>Bill Number</TableCell>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Karigar</TableCell>
                    <TableCell>Karat</TableCell>
                    <TableCell>Item Weight</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Issue Date</TableCell>
                    <TableCell>Remarks</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row, idx) => {
                    const itemKey = row.orderId + '-' + row.itemId;
                    return (
                      <TableRow key={itemKey}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selected.includes(itemKey)}
                            onChange={e => handleSelect(itemKey, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>{row.billName}</TableCell>
                        <TableCell>{row.billNumber}</TableCell>
                        <TableCell>{row.itemName}</TableCell>
                        <TableCell>{row.karigar}</TableCell>
                        <TableCell>{row.karat}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.itemWeight}
                            type="number"
                            label="Item Weight"
                            sx={{ width: 100 }}
                            InputProps={{ readOnly: true }}
                          />
                        </TableCell>
                        <TableCell>{row.quantity}</TableCell>
                        <TableCell>
                          <Box>
                            <TextField
                              size="small"
                              value={issueDetails[itemKey]?.date || ''}
                              onChange={e => handleInputChange(itemKey, 'date', e.target.value)}
                              label="Issue Date"
                              sx={{ width: 140 }}
                              disabled={!selected.includes(itemKey)}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={issueDetails[itemKey]?.remarks || ''}
                            onChange={e => handleInputChange(itemKey, 'remarks', e.target.value)}
                            label="Remarks"
                            sx={{ width: 120 }}
                            disabled={!selected.includes(itemKey)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={selected.length === 0}
                onClick={handleIssueSelected}
              >
                Issue Selected
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ItemIssue; 